import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  IUseUpdateUserCredentials,
  IUseUpdateUserResponse,
  UpdateUserVariables,
} from "../../../models/Interface/User.interface";
import axiosInstance, { CustomAxiosError } from "../../../utils/axios";
import { IErrorResponse, QueryKeys } from "../../querys/querys.enum";
import { MutationKeys } from "../../mutation/mutation.enum";
import { AxiosError } from "axios";

const useUpdateSubUserReq = async (
  userId: string,
  data: Partial<IUseUpdateUserCredentials>
) => {
  try {
    const response = await axiosInstance.put<IUseUpdateUserResponse>(
      `users/update/sub/${userId}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error in useUpdateSubUserReq:", error);
    throw error;
  }
};

const useUpdateSubUser = (
  options?: Partial<
    UseMutationOptions<
      IUseUpdateUserResponse,
      AxiosError<IErrorResponse>,
      UpdateUserVariables
    >
  >
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IUseUpdateUserResponse,
    AxiosError<IErrorResponse>,
    UpdateUserVariables
  >({
    mutationKey: [MutationKeys.UPDATE_SUB_USER],
    mutationFn: async ({ userId, data }) => {
      try {
        return await useUpdateSubUserReq(userId, { ...data });
      } catch (error) {
        console.error("Error in useUpdateSubUser mutationFn:", error);
        throw error;
      }
    },
    retry: 0, // Don't retry on failure
    ...options,
    onSuccess: (data, variables, cx) => {
      try {
        // Update cache data
        queryClient.setQueryData([QueryKeys.SUB_USER], data.user);
        
        // Show success message
        alert("SubUser updated successfully...");
        
        // Call user-provided onSuccess handler
        if (options?.onSuccess) {
          options.onSuccess(data, variables, cx);
        }
      } catch (successError) {
        console.error("Error in onSuccess handler:", successError);
      }
    },
    onError: (error: CustomAxiosError, variables, context) => {
      try {
        // Log the error
        console.error("Update SubUser error:", error?.response?.data);
        
        // Show error message
        alert(error?.response?.data?.message || "An error occurred during update");
        
        // Call user-provided onError handler
        if (options?.onError) {
          options.onError(
            error as AxiosError<IErrorResponse>,
            variables,
            context
          );
        }
      } catch (errorHandlingError) {
        console.error("Error in onError handler:", errorHandlingError);
      }
    },
  });
};

export default useUpdateSubUser;
